import React from "react";
import { StatusBubble } from "./StatusBubble";
import { PaperAirplaneIcon, PlusIcon } from "@heroicons/react/24/solid";
import {
  EyeIcon,
  ClipboardDocumentIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";

const DEFAULT_LABELS = {
  "view": "View",
  "download": "Download",
  "payLinkDraft": "PayLink Draft",
  "payLinkSent": "PayLink Sent",
  "payLinkOpened": "PayLink Opened",
  "payLinkPaid": "PayLink Paid",
  "payLinkExpired": "PayLink Expired",
  "payLink": "PayLink",
  "sending": "Sending…",
  "send": "Send",
  "payLinkAction": "Pay Link",
  "upgradePro": "Upgrade Pro",
  "viewInvoice": "View invoice",
  "downloadInvoice": "Download invoice HTML",
  "openPayLink": "Open pay link",
  "sendInvoice": "Send invoice",
  "cannotSend": "Cannot send invoice",
  "addCustomerDetails": "Add customer details",
  "date": "Date",
  "dueDate": "Due",
  "invoice": "Invoice",
  "customer": "Customer",
  "outstanding": "Outstanding",
  "total": "Total"
};

export type SaleInfoHeaderLabels = Partial<typeof DEFAULT_LABELS>;

type BubbleType =
  | "preorder"
  | "fulfilled"
  | "paid"
  | "partial"
  | "unpaid"
  | "voided";

type BubbleInfo = { id?: BubbleType; label: string; bubbleType: BubbleType };

type InvoiceActionGroupProps = {
  onViewInvoice?: () => void;
  onDownloadInvoice?: () => void;
  onPayLink?: () => void;
  showPayLinkAction?: boolean;
  onSendInvoice?: () => void;
  canSend?: boolean;
  isSending?: boolean;
  blockedReason?: string;
  onUpgrade?: () => void;
  requiresPro?: boolean;
  labels?: SaleInfoHeaderLabels;
};

function money(n: number | undefined) {
  return typeof n === "number" && !Number.isNaN(n)
    ? formatMoney(n, "$", { minDp: 0, maxDp: 2 })
    : "—";
}

function hasValue(v: unknown) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function normalizePayLinkStatus(
  value?: string | null,
): "draft" | "sent" | "open" | "paid" | "expired" | null {
  const s = (value || "").trim().toLowerCase();
  if (!s) return null;

  if (["draft", "created", "ready"].includes(s)) return "draft";
  if (["sent", "emailed"].includes(s)) return "sent";
  if (["open", "opened", "viewed"].includes(s)) return "open";
  if (["paid", "completed", "succeeded"].includes(s)) return "paid";
  if (["expired", "void", "voided", "cancelled", "canceled"].includes(s)) {
    return "expired";
  }

  return "sent";
}

function getPayLinkPill(status: string | null | undefined, labels: typeof DEFAULT_LABELS) {
  const normalized = normalizePayLinkStatus(status);

  switch (normalized) {
    case "draft":
      return {
        label: labels.payLinkDraft,
        className: "bg-gray-50 text-gray-700 ring-gray-200",
      };
    case "sent":
      return {
        label: labels.payLinkSent,
        className: "bg-amber-50 text-amber-700 ring-amber-100",
      };
    case "open":
      return {
        label: labels.payLinkOpened,
        className: "bg-sky-50 text-sky-700 ring-sky-100",
      };
    case "paid":
      return {
        label: labels.payLinkPaid,
        className: "bg-emerald-50 text-emerald-700 ring-emerald-100",
      };
    case "expired":
      return {
        label: labels.payLinkExpired,
        className: "bg-red-50 text-red-700 ring-red-100",
      };
    default:
      return {
        label: labels.payLink,
        className: "bg-amber-50 text-amber-700 ring-amber-100",
      };
  }
}

function InvoiceActionGroup({
  onViewInvoice,
  onDownloadInvoice,
  onPayLink,
  showPayLinkAction,
  onSendInvoice,
  canSend,
  isSending,
  blockedReason,
  onUpgrade,
  requiresPro = false,
  labels: displayLabels,
}: InvoiceActionGroupProps) {
  if (!onViewInvoice && !onDownloadInvoice && !onPayLink && !onSendInvoice) {
    return null;
  }

  const labels = { ...DEFAULT_LABELS, ...displayLabels };

  const miniBtn =
    "inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white " +
    "px-2.5 py-1.5 text-xs font-medium leading-none text-gray-700 " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  const sendBtnBase =
    "inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-medium leading-none transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <span className="inline-flex items-center gap-1.5 align-middle group relative flex-wrap">
      {onViewInvoice && (
        <button
          type="button"
          onClick={onViewInvoice}
          className={miniBtn}
          aria-label={labels.viewInvoice}
          title={labels.viewInvoice}
        >
          <EyeIcon className="w-3.5 h-3.5 text-gray-400" />
          <span className="hidden sm:inline">{labels.view}</span>
        </button>
      )}

      {onDownloadInvoice && (
        <button
          type="button"
          onClick={onDownloadInvoice}
          className={miniBtn}
          aria-label={labels.downloadInvoice}
          title={labels.downloadInvoice}
        >
          <ClipboardDocumentIcon className="w-3.5 h-3.5 text-gray-400" />
          <span className="hidden sm:inline">{labels.download}</span>
        </button>
      )}

      {showPayLinkAction && onPayLink && (
        <button
          type="button"
          onClick={onPayLink}
          className={miniBtn}
          aria-label={labels.openPayLink}
          title={labels.openPayLink}
        >
          <LinkIcon className="w-3.5 h-3.5 text-gray-400" />
          <span className="hidden sm:inline">{labels.payLinkAction}</span>
        </button>
      )}

      {onSendInvoice && (
        <span className="inline-flex items-center">
          <button
            type="button"
            onClick={onSendInvoice}
            disabled={!canSend || !!isSending}
            className={
              sendBtnBase +
              " " +
              (canSend
                ? "bg-appleblue text-white hover:bg-appleblueD active:bg-appleblueDD"
                : "bg-gray-200 text-gray-500")
            }
            aria-label={
              canSend ? labels.sendInvoice : blockedReason || labels.cannotSend
            }
          >
            <PaperAirplaneIcon
              className={
                "w-3.5 h-3.5 -rotate-12 " +
                (isSending ? "opacity-40 animate-pulse" : "")
              }
            />
            <span className="hidden sm:inline">
              {isSending ? labels.sending : labels.send}
            </span>
          </button>

          {!canSend && !!blockedReason && (
            <span
              className="
                ml-2 text-xs whitespace-nowrap
                opacity-0 pointer-events-none
                group-hover:opacity-100 group-hover:pointer-events-auto
                group-focus-within:opacity-100 group-focus-within:pointer-events-auto
                transition-opacity
              "
            >
              {requiresPro ? (
                <button
                  type="button"
                  className="text-appleblue underline underline-offset-2 hover:text-appleblueD focus:outline-none"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onUpgrade?.();
                  }}
                >
                  {labels.upgradePro}
                </button>
              ) : (
                <span className="text-gray-600">{blockedReason}</span>
              )}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

export function SaleInfoHeader({
  date,
  invoice,
  invoiceId,
  customer,
  email,
  mobile,
  statusBubbles,
  edited,
  lastEditedAt,
  outstanding,
  dueDate,
  showInvoiceLink = false,
  total,
  isPaid,
  className = "",
  showPayLinkPill = false,
  payLinkStatus = null,

  onViewInvoice,
  onDownloadInvoice,
  onPayLink,
  showPayLinkAction = false,
  onSendInvoice,
  canSendInvoice,
  sendingInvoice,
  sendBlockedReason,
  onUpgrade,
  onAddCustomer,
  showAddCustomer = false,
  requiresPro = false,
  labels: displayLabels,
}: {
  date: string;
  invoice?: string | null;
  invoiceId?: string | number | null;
  customer?: string | null;
  email?: string | null;
  mobile?: string | null;
  statusBubbles: BubbleInfo[];
  edited?: boolean;
  lastEditedAt?: string;
  outstanding?: number;
  dueDate?: string;
  showInvoiceLink?: boolean;
  total?: number;
  isPaid?: boolean;
  className?: string;
  showPayLinkPill?: boolean;
  payLinkStatus?: string | null;

  onViewInvoice?: () => void;
  onDownloadInvoice?: () => void;
  onPayLink?: () => void;
  showPayLinkAction?: boolean;
  onSendInvoice?: () => void;
  canSendInvoice?: boolean;
  sendingInvoice?: boolean;
  sendBlockedReason?: string;
  onUpgrade?: () => void;
  requiresPro?: boolean;
  labels?: SaleInfoHeaderLabels;
  onAddCustomer?: () => void;
  showAddCustomer?: boolean;
}) {
  const labels = { ...DEFAULT_LABELS, ...displayLabels };
  const canSend = !!canSendInvoice;
  const isSending = !!sendingInvoice;

  const showInvoiceValue = hasValue(invoice) || hasValue(invoiceId);
  const showCustomer = hasValue(customer);
  const showEmail = hasValue(email);
  const showMobile = hasValue(mobile);

  const showOutstanding =
    typeof outstanding === "number" &&
    !Number.isNaN(outstanding) &&
    outstanding > 0.004;
  const showDueDate = hasValue(dueDate) && !isPaid;
  const showTotal = typeof total === "number" && !Number.isNaN(total);

  const shouldShowPayLinkPill = !!showPayLinkPill || !!payLinkStatus;
  const payLinkPill = getPayLinkPill(payLinkStatus, labels);

  const rowLabelClass = `${A.label} min-h-5 flex items-start pt-0.5`;
  const rowValueClass = `min-h-5 flex items-center text-gray-700`;
  const rowValueNumClass = `${A.num} min-h-5 flex items-center text-gray-700`;

  return (
    <div className={`flex flex-col gap-3 mb-4 ${className}`}>
      <div
        className={`flex flex-col gap-3 md:flex-row md:items-start md:justify-between ${A.body}`}
      >
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-[max-content_minmax(0,1fr)] gap-x-2 gap-y-1 text-[13px]">
            <div className={rowLabelClass}>{labels.date}</div>
            <div className={rowValueNumClass}>{formatAnyDateToLocale(date)}</div>

            {showInvoiceValue && (
              <>
                <div className={rowLabelClass}>{labels.invoice}</div>
                <div className={`${rowValueNumClass} break-words`}>
                  {invoice || invoiceId}
                </div>
              </>
            )}

            {showCustomer && (
              <>
                <div className={rowLabelClass}>{labels.customer}</div>
                <div className="min-h-5 flex flex-col justify-center">
                  <div className={`${rowValueClass} break-words gap-1.5`}>
                    <span>{customer}</span>

                    {showAddCustomer && onAddCustomer && (
                      <button
                        type="button"
                        onClick={onAddCustomer}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-appleblue hover:bg-appleblue/10 focus:outline-none focus:ring-2 focus:ring-appleblueD"
                        aria-label={labels.addCustomerDetails}
                        title={labels.addCustomerDetails}
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {showEmail && (
                    <div className="text-xs text-gray-500 break-all">
                      {email}
                    </div>
                  )}

                  {showMobile && (
                    <div className={`text-xs text-gray-500 break-all ${A.num}`}>
                      {mobile}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {showInvoiceLink && invoiceId && (
          <div className="shrink-0 self-start md:self-start md:ml-4">
            <InvoiceActionGroup
              onViewInvoice={onViewInvoice}
              onDownloadInvoice={onDownloadInvoice}
              onPayLink={onPayLink}
              showPayLinkAction={showPayLinkAction}
              onSendInvoice={onSendInvoice}
              canSend={canSend}
              isSending={isSending}
              blockedReason={sendBlockedReason}
              onUpgrade={onUpgrade}
              requiresPro={requiresPro}
              labels={labels}
            />
          </div>
        )}
      </div>

      <div className={`flex flex-wrap items-center gap-2 ${A.body}`}>
        {statusBubbles.map((b, i) => (
          <StatusBubble
            key={`${b.id ?? b.bubbleType}-${i}`}
            label={b.label}
            bubbleType={b.bubbleType}
          />
        ))}

        {shouldShowPayLinkPill && (
          <span
            className={`${A.pillBase} ${payLinkPill.className}`}
            aria-label={payLinkPill.label}
          >
            {payLinkPill.label}
          </span>
        )}

        {showOutstanding && (
          <span className={`inline-flex items-center gap-1 ${A.label}`}>
            {labels.outstanding}
            <span className={`font-medium text-gray-700 ${A.num}`}>
              {money(outstanding)}
            </span>
          </span>
        )}

        {showDueDate && (
          <span className={`inline-flex items-center gap-1 ${A.label}`}>
            {labels.dueDate}
            <span className={`font-medium text-gray-700 ${A.num}`}>
              {formatAnyDateToLocale(dueDate)}
            </span>
          </span>
        )}

        {showTotal && (
          <span className={`inline-flex items-center gap-1 ${A.label}`}>
            {labels.total}
            <span className={`font-semibold text-gray-700 ${A.num}`}>
              {money(total)}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}